import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  InputAdornment,
} from '@mui/material';

interface RefundRequestFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RefundFormData) => void;
}

export interface RefundFormData {
  orderId: string;
  customerName: string;
  amount: number;
  reason: string;
}

interface FormErrors {
  orderId?: string;
  customerName?: string;
  amount?: string;
  reason?: string;
}

const RefundRequestForm: React.FC<RefundRequestFormProps> = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<RefundFormData>({
    orderId: '',
    customerName: '',
    amount: 0,
    reason: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.orderId) {
      newErrors.orderId = 'Order ID is required';
    }
    if (!formData.customerName) {
      newErrors.customerName = 'Customer name is required';
    }
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    if (!formData.reason) {
      newErrors.reason = 'Reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
      onClose();
      setFormData({
        orderId: '',
        customerName: '',
        amount: 0,
        reason: '',
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof RefundFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Refund Request</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="orderId"
                label="Order ID"
                value={formData.orderId}
                onChange={handleChange}
                fullWidth
                error={!!errors.orderId}
                helperText={errors.orderId}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="customerName"
                label="Customer Name"
                value={formData.customerName}
                onChange={handleChange}
                fullWidth
                error={!!errors.customerName}
                helperText={errors.customerName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="amount"
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                fullWidth
                error={!!errors.amount}
                helperText={errors.amount}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="reason"
                label="Reason"
                value={formData.reason}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                error={!!errors.reason}
                helperText={errors.reason}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RefundRequestForm;
